/**
 * Helper to manage biometrics preference in localStorage.
 * This persists across app sessions to remember user's choice.
 * X6: All reads/writes wrapped in try/catch for private browsing / quota errors.
 */

import { storageSet, storageRemove } from '../platform/storage';

const BIOMETRICS_ENABLED_KEY = 'mailander_biometrics_enabled';
const BIOMETRICS_TYPE_KEY = 'mailander_biometrics_type';
const AUTO_PROMPT_PASSKEY_KEY = 'mailander_auto_prompt_passkey';
const BIOMETRICS_EMAIL_KEY = 'mailander_biometrics_email';

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  storageSet(key, value);
}

function safeRemove(key: string): void {
  storageRemove(key);
}

export const BiometricsStorage = {
  /**
   * Get whether biometrics is enabled for this device
   */
  isBiometricsEnabled(): boolean {
    return safeGet(BIOMETRICS_ENABLED_KEY) === 'true';
  },

  /**
   * Set biometrics enabled status
   */
  setBiometricsEnabled(enabled: boolean): void {
    if (enabled) {
      safeSet(BIOMETRICS_ENABLED_KEY, 'true');
    } else {
      safeRemove(BIOMETRICS_ENABLED_KEY);
    }
  },

  /**
   * Get the biometrics type (face, fingerprint, unknown)
   */
  getBiometricsType(): 'face' | 'fingerprint' | 'unknown' | null {
    const value = safeGet(BIOMETRICS_TYPE_KEY);
    return (value as 'face' | 'fingerprint' | 'unknown') || null;
  },

  /**
   * Set the biometrics type
   */
  setBiometricsType(type: 'face' | 'fingerprint' | 'unknown' | null): void {
    if (type) {
      safeSet(BIOMETRICS_TYPE_KEY, type);
    } else {
      safeRemove(BIOMETRICS_TYPE_KEY);
    }
  },

  /**
   * Get whether auto-prompt passkey is enabled
   */
  isAutoPromptPasskeyEnabled(): boolean {
    return safeGet(AUTO_PROMPT_PASSKEY_KEY) === 'true';
  },

  /**
   * Set auto-prompt passkey status
   */
  setAutoPromptPasskey(enabled: boolean): void {
    if (enabled) {
      safeSet(AUTO_PROMPT_PASSKEY_KEY, 'true');
    } else {
      safeRemove(AUTO_PROMPT_PASSKEY_KEY);
    }
  },

  /**
   * Get the stored email for targeted WebAuthn authentication
   */
  getEmail(): string | null {
    return safeGet(BIOMETRICS_EMAIL_KEY);
  },

  /**
   * Store email so biometric login can pass it for targeted authentication
   * (avoids discoverable credential flow on Android Chrome which shows the
   * Google Credential Manager "passkey" UI instead of fingerprint prompt)
   */
  setEmail(email: string | null): void {
    if (email) {
      safeSet(BIOMETRICS_EMAIL_KEY, email);
    } else {
      safeRemove(BIOMETRICS_EMAIL_KEY);
    }
  },

  /**
   * Clear all biometrics preferences
   */
  clear(): void {
    safeRemove(BIOMETRICS_ENABLED_KEY);
    safeRemove(BIOMETRICS_TYPE_KEY);
    safeRemove(AUTO_PROMPT_PASSKEY_KEY);
    safeRemove(BIOMETRICS_EMAIL_KEY);
  },
};
